/* eslint-disable security/detect-object-injection */
import dayjs from "dayjs";
import { isEqual } from "lodash";
import * as yup from "yup";
import { CaseReassignDetails, DashboardDetails, FilterOptions } from "./interface";
import { OPTIONS } from "./resources";

const filterOptionsInitialValue: FilterOptions = {
  loanStatus: Array(OPTIONS.filterLoanStatus?.length).fill(false),
  proposalStatus: Array(OPTIONS.filterProposalStatus?.length).fill(false),
  paymentStatus: Array(OPTIONS.paymentStatus?.length).fill(false),
  sparkDecision: Array(OPTIONS.sparkDecision?.length).fill(false),
  sumAssured: [1000000, 100000000],
  premium: [1000, 5000000],
  policyTerm: "",
  product: ""
};

const initialValues: DashboardDetails = {
  searchQuery: "",
  filterOptions: filterOptionsInitialValue,
  actionType: "",
  formType: ""
};

const caseReassignInitialValue: CaseReassignDetails = {
  supervisor: ""
};

const caseReassignValidationSchema = () =>
  yup.object().shape<Record<keyof CaseReassignDetails, yup.AnySchema>>({
    supervisor: yup.string().required("Please Select Option")
  });

const constructFilterQuery = (filterBy: FilterOptions) => ({
  ...(!isEqual(filterBy.loanStatus, filterOptionsInitialValue.loanStatus) && {
    loanStatus: filterBy.loanStatus
      ?.reduce((prev: string[], curr, index) => {
        if (curr) prev.push(OPTIONS.filterLoanStatus[index]);
        return prev;
      }, [])
      ?.join(",")
  }),
  ...(!isEqual(filterBy.proposalStatus, filterOptionsInitialValue.proposalStatus) && {
    proposalStatus: filterBy.proposalStatus
      ?.reduce((prev: string[], curr, index) => {
        if (curr) prev.push(OPTIONS.filterProposalStatus[index]);
        return prev;
      }, [])
      ?.join(",")
  }),
  ...(!isEqual(filterBy.paymentStatus, filterOptionsInitialValue.paymentStatus) &&
    filterBy?.paymentStatus?.some(Boolean) && {
      paymentStatus: filterBy.paymentStatus
        ?.reduce((prev: string[], curr, index) => {
          if (curr) prev.push(OPTIONS.filterPaymentStatus[index]);
          return prev;
        }, [])
        ?.join(",")
    }),
  ...(!isEqual(filterBy.sparkDecision, filterOptionsInitialValue.sparkDecision) &&
    filterBy?.sparkDecision?.some(Boolean) && {
      sparkFullDecision: filterBy?.sparkDecision
        ?.reduce((prev: string[], curr, index) => {
          if (curr) prev.push(OPTIONS?.filterSparkDecision[index]?.code);
          return prev;
        }, [])
        ?.join(",")
    }),
  ...(!isEqual(filterBy.sumAssured, filterOptionsInitialValue.sumAssured) && {
    sumAssured: filterBy.sumAssured?.join(",")
  }),
  ...(!isEqual(filterBy.premium, filterOptionsInitialValue.premium) && {
    totalPremium: filterBy.premium?.join(",")
  }),
  ...(filterBy.policyTerm && { policyTerm: filterBy.policyTerm }),
  ...(filterBy.product && {
    productId: filterBy.product
  })
});

const downloadPDF = (pdf: any, leadId: string) => {
  const linkSource = `data:application/pdf;base64,${pdf}`;
  const downloadLink = document.createElement("a");
  const fileName = `spark_${leadId}`;
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

// eslint-disable-next-line import/prefer-default-export
export {
  caseReassignInitialValue,
  caseReassignValidationSchema,
  constructFilterQuery,
  downloadPDF,
  filterOptionsInitialValue,
  initialValues
};
export const statusToBucketStringMap: Record<string, string> = {
  Decline_Verification: "OPS-UW",
  Assessment: "OPS-NB",
  Grevience: "OPS-UW",
  Pre_Issuance_QC: "OPS-NB",
  SecondaryUW: "OPS-UW",
  Primary_UW: "OPS-UW",
  UW_Req_Receive: "OPS-NB",
  RCUAndFraud: "OPS-UW",
  Data_Entry_Accept: "OPS-NB",
  Expert: "OPS-UW",
  Data_Entry: "OPS-NB",
  Assessment_Accept: "OPS-NB",
  Error: "OPS-NB",
  ECGAndCTMT: "OPS-UW",
  SPR_Rule: "OPS-NB",
  PrimaryUWIntegration: "OPS-UW",
  MedicalQC: "OPS-UW",
  Create_Update_Req: "OPS-UW",
  HOD: "OPS-UW",
  Refund_Trigger: "OPS-NB",
  Rollback: "OPS-NB",
  DRC: "OPS-NB",
  Assessment_Change: "OPS-NB",
  STPQCSampelling: "OPS-NB",
  SecondaryUWIntegration: "OPS-UW",
  BRMS: "OPS-NB",
  STP_Req_Receive: "OPS-NB",
  IIB: "OPS-NB",
  Issuance: "PENDING FOR PAYMENT",
  SAR_Req_Update: "PENDING FOR PAYMENT",
  UWReqScanning: "Sales Pending",
  STPReqScanning: "Sales Pending",
  Temp_UW: "OPS-UW",
  Create_Update_CO: "OPS-UW",
  ReinsuranceRefferal: "OPS-UW",
  CMO: "OPS-UW"
};

export const dashboardSearchOnOptions = ["IRM Name", "Application No", "CLC App No"];

export const dashboardSearchOnMapper: any = {
  "IRM Name": "irmName",
  "Application No": "applicationNo",
  "CLC App No": "clcAppNo"
};

type DateRangeDirection = "past" | "future" | "both";

export const getDateRangeFromToday = (
  rangeInDays: number,
  direction: DateRangeDirection = "past"
) => {
  const today = dayjs();

  switch (direction) {
    case "future":
      return {
        minDate: today.startOf("day"),
        maxDate: today.add(rangeInDays, "day").endOf("day")
      };
    case "both":
      return {
        minDate: today.subtract(rangeInDays, "day").startOf("day"),
        maxDate: today.add(rangeInDays, "day").endOf("day")
      };
    case "past":
    default:
      return {
        minDate: today.subtract(rangeInDays, "day").startOf("day"),
        maxDate: today.endOf("day")
      };
  }
};
